import React from 'react';
import { Box, ButtonBase, Stack, Typography } from '@mui/material';
import InsertChartOutlinedIcon from '@mui/icons-material/InsertChartOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import { Button } from '../../../../components/ui';
import { useFeatureTheme } from '../../../../theme/hooks/useFeatureTheme';
import { ANALYTICS_COPY } from '../../constants/analyticsCopy';
import { DATASET_INTERVENE, DATASET_WHATSAPP } from '../../constants/datasetScope';

const SOURCES = [
  {
    id: 'records',
    title: ANALYTICS_COPY.sourceRecordsTitle,
    description: ANALYTICS_COPY.sourceRecordsDescription,
    Icon: InsertChartOutlinedIcon,
  },
  {
    id: DATASET_WHATSAPP,
    title: ANALYTICS_COPY.sourceWhatsappTitle,
    description: ANALYTICS_COPY.sourceWhatsappDescription,
    Icon: SendOutlinedIcon,
  },
  {
    id: DATASET_INTERVENE,
    title: ANALYTICS_COPY.sourceInterveneTitle,
    description: ANALYTICS_COPY.sourceInterveneDescription,
    Icon: ForumOutlinedIcon,
  },
];

export default function WelcomePanel({ onCreateReport, onSelectSource, onBrowseTemplates }) {
  const { semantic, typography, radius, shadows } = useFeatureTheme();

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: semantic.borders.border,
        borderRadius: `${radius.lg}px`,
        bgcolor: semantic.surfaces.surface,
        boxShadow: shadows.sm,
        px: { xs: 2, sm: 4 },
        py: { xs: 4, sm: 5 },
      }}
    >
      <Stack
        alignItems="center"
        spacing={3}
        sx={{ maxWidth: 840, mx: 'auto', textAlign: 'center' }}
      >
        <Stack spacing={1} alignItems="center" sx={{ maxWidth: 460 }}>
          <Typography
            sx={{
              fontSize: typography.fontSizeXl,
              fontWeight: typography.fontWeightSemibold,
              lineHeight: typography.lineHeightHeading,
              color: semantic.text.primary,
            }}
          >
            {ANALYTICS_COPY.welcomeTitle}
          </Typography>
          <Typography
            sx={{
              fontSize: typography.fontSizeMd,
              lineHeight: typography.lineHeightBody,
              color: semantic.text.secondary,
            }}
          >
            {ANALYTICS_COPY.welcomeSubtitle}
          </Typography>
        </Stack>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          justifyContent="center"
        >
          <Button variant="contained" onClick={() => onCreateReport?.()}>
            {ANALYTICS_COPY.createFirstReport}
          </Button>
          {onBrowseTemplates ? (
            <Button variant="outlined" onClick={onBrowseTemplates}>
              {ANALYTICS_COPY.browseTemplates}
            </Button>
          ) : null}
        </Stack>

        <Box sx={{ width: '100%', pt: 1 }}>
          <Typography
            sx={{
              fontSize: typography.fontSizeMd,
              fontWeight: typography.fontWeightMedium,
              color: semantic.text.secondary,
              mb: 1.5,
            }}
          >
            {ANALYTICS_COPY.welcomeSourcesTitle}
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
              gap: 1.5,
              textAlign: 'left',
            }}
          >
            {SOURCES.map((source) => {
              const Icon = source.Icon;
              return (
                <ButtonBase
                  key={source.id}
                  onClick={() => (onSelectSource || onCreateReport)?.(source.id)}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    width: '100%',
                    p: 2,
                    borderRadius: `${radius.md}px`,
                    border: '1px solid',
                    borderColor: semantic.borders.border,
                    bgcolor: semantic.surfaces.surfaceSubtle,
                    transition: 'border-color 0.15s ease, background-color 0.15s ease',
                    '&:hover': {
                      borderColor: semantic.interactive.primary,
                      bgcolor: semantic.interactive.primarySoft,
                    },
                    '&.Mui-focusVisible': {
                      outline: `2px solid ${semantic.interactive.primary}`,
                      outlineOffset: 2,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 36,
                      height: 36,
                      flexShrink: 0,
                      borderRadius: `${radius.sm}px`,
                      bgcolor: semantic.surfaces.surface,
                      color: semantic.interactive.primary,
                      border: '1px solid',
                      borderColor: semantic.borders.border,
                    }}
                  >
                    <Icon sx={{ fontSize: typography.fontSize2xl }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: typography.fontSizeMd,
                        fontWeight: typography.fontWeightSemibold,
                        color: semantic.text.primary,
                        lineHeight: typography.lineHeightHeading,
                      }}
                    >
                      {source.title}
                    </Typography>
                    <Typography
                      sx={{
                        mt: 0.5,
                        fontSize: typography.fontSizeMd,
                        lineHeight: typography.lineHeightBody,
                        color: semantic.text.secondary,
                      }}
                    >
                      {source.description}
                    </Typography>
                  </Box>
                </ButtonBase>
              );
            })}
          </Box>
        </Box>
      </Stack>
    </Box>
  );
}
